import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Check } from "lucide-react";
import collectPayments from "../../../assets/collect-payments.gif";
import { useNavigate } from "react-router-dom";

const SimplePayments = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const navigate = useNavigate();

  const handleNavigate = () => {
    navigate("/register");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 100,
      },
    },
  };

  return (
    <section className="py-16 bg-gradient-to-br from-white to-red-50">
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
            className="lg:max-w-xl lg:ml-12 xl:ml-24 text-center lg:text-left"
          >
            <motion.h2
              variants={itemVariants}
              className="text-3xl md:text-4xl xl:text-5xl font-bold text-red-900 mb-4"
            >
              Seamless Payments, Effortless Growth
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-lg md:text-xl text-gray-700 mb-6 max-w-lg mx-auto lg:mx-0"
            >
              Your business journey has enough challenges. Getting paid
              shouldn't be one of them.
            </motion.p>

            <motion.div variants={itemVariants}>
              <a
                href="#getstarted"
                className="inline-block bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-300"
                onClick={handleNavigate}
              >
                Start Accepting Payments
              </a>
            </motion.div>

            <motion.div variants={itemVariants} className="mt-8 text-left">
              <h3 className="text-xl md:text-2xl font-semibold text-red-800 mb-2">
                Turn payments into a positive part of the customer journey
              </h3>
              <p className="text-gray-700 mb-4">
                Give your customers the freedom to pay with ease and comfort.
                With just one integration, VasPay lets them choose how they pay,
                making every transaction feel effortless.
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {[
                  "Card",
                  "Apple Pay",
                  "Bank Account",
                  "Visa QR",
                  "Mobile Money",
                  "USSD",
                  "POS",
                ].map((method, index) => (
                  <motion.li
                    key={index}
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          delay: 0.4 + index * 0.1,
                          duration: 0.5,
                        },
                      },
                    }}
                    className="flex items-center"
                  >
                    <span className="flex items-center justify-center bg-red-100 rounded-full p-1 mr-3">
                      <Check className="h-4 w-4 text-red-500" />
                    </span>
                    <span className="text-gray-700">{method}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-12 lg:mt-0 w-full max-w-md lg:max-w-lg"
          >
            <div className="p-6 md:p-8">
              <img
                src={collectPayments || "/placeholder.svg"}
                alt="Collect payments gif"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SimplePayments;
